import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getLogger } from '@storefront/logging';
import { LDClient, initialize } from 'launchdarkly-js-client-sdk';
import {
  asyncWithLDProvider,
  useLDClient,
} from 'launchdarkly-react-client-sdk';
import { getSession } from 'next-auth/react';
import { env } from 'next-runtime-env';

interface LaunchDarklyProps {
  children: ReactElement<any, any>;
  ldClient: LDClient;
}

const logger = getLogger('launchdarkly');
let ldClient: LDClient;

const ConfiglessLDProvider = ({ children }: LaunchDarklyProps) => {
  const ldClient = useLDClient();

  useEffect(() => {
    const identifyUser = async () => {
      const session = await getSession();
      if (!session) return;
      const access_token = session['access_token'];
      if (!access_token) return;
      ldClient.identify(
        {
          key: access_token,
        },
        'base64'
      );
    };
    if (ldClient) identifyUser();
  }, [ldClient]);

  return children;
};

type LaunchDarklyProviderType = {
  children: React.ReactNode;
  clientSideID: string;
};

const LaunchDarklyProvider = ({
  children,
  clientSideID,
}: LaunchDarklyProviderType) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const LDProviderRef =
    useRef<({ children }: { children: ReactNode }) => ReactElement | null>(
      null
    );
  const init = async () =>
    await asyncWithLDProvider({
      clientSideID,
      options: {
        bootstrap: 'localStorage',
        streaming: false,
        logger,
      },
      reactOptions: {
        useCamelCaseFlagKeys: true,
      },
    });
  useEffect(() => {
    const handleInit = async () => {
      LDProviderRef.current = await init();
      setIsLoaded(true);
    };
    handleInit();
  }, []);

  if (!isLoaded) {
    logger.info('No LaunchDarkly Provider yet...');
    return null;
  }
  return React.createElement(
    LDProviderRef.current,
    null,
    React.createElement(ConfiglessLDProvider, null, children)
  );
};

// Client-side use
export async function getFeatureFlagForAnonymous<T>(
  flag: string,
  defaultValue: T
): Promise<T> {
  if (typeof window !== 'undefined') {
    try {
      if (!ldClient) {
        const clientSideID = env('NEXT_PUBLIC_LAUNCHDARKLY_CLIENTSIDE_ID');
        if (!clientSideID) {
          logger.warn('LaunchDarkly client-side ID not found');
          return defaultValue;
        }
        ldClient = initialize(
          env('NEXT_PUBLIC_LAUNCHDARKLY_CLIENTSIDE_ID'),
          {
            anonymous: true,
            key: 'user',
          },
          {
            logger,
          }
        );
      }
      await ldClient.waitForInitialization();
      return ldClient.variation(flag, defaultValue);
    } catch (err) {
      logger.error(err);
      return defaultValue;
    }
  }
  return defaultValue;
}

export { LaunchDarklyProvider, ConfiglessLDProvider };
